// src/components/Notifications.jsx
// App-wide in-page messages, replacing the browser's alert() and confirm():
//   const { notify, confirm } = useNotify();
//   notify({ tone: 'error', title: 'Download failed', detail: e.message });
//   if (await confirm({ title: 'Delete this article?', confirmLabel: 'Delete', danger: true })) …
// notify() shows a notice in the bottom-left corner (see Toasts.jsx).
// bell: 'fax' | 'people' (3.59.2) also keeps it in the notification bell.
// confirm() opens a small dialog and resolves true (confirmed) or false.
// With choices: [{ value, label, hint? }] it also asks which one (radio
// buttons, first picked) and resolves the chosen value, or false.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Toasts from './Toasts';
import { emoLabel } from './Emo';

const NotifyContext = createContext(null);
let nextId = 1;

export function NotifyProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);

  const dismiss = useCallback(id => setToasts(t => t.filter(x => x.id !== id)), []);
  const notify  = useCallback(toast => {
    setToasts(t => [...t, { tone: 'info', ...toast, id: nextId++ }].slice(-4));
    // 3.59.2 (owner): fax actions and people actions (bell: 'fax' | 'people')
    // are also kept in the bell, on the account, for 7 days, listed but not
    // counted (components/NotificationBell.jsx); errors never are
    if (toast?.bell && toast.tone !== 'error') {
      const text = v => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');
      window.dispatchEvent(new CustomEvent('bell-alert', { detail: { group: toast.bell, tone: toast.tone || 'info', title: text(toast.title), detail: text(toast.detail) } }));
    }
  }, []);
  const confirm = useCallback(opts => new Promise(resolve => setDialog({ ...opts, resolve })), []);

  const value = useMemo(() => ({ notify, confirm }), [notify, confirm]);

  return (
    <NotifyContext.Provider value={value}>
      {children}
      <Toasts toasts={toasts} onDismiss={dismiss} />
      {dialog && (
        <ConfirmDialog {...dialog} onClose={ok => { dialog.resolve(ok); setDialog(null); }} />
      )}
    </NotifyContext.Provider>
  );
}

export function useNotify() {
  const ctx = useContext(NotifyContext);
  if (!ctx) throw new Error('useNotify must be used inside NotifyProvider');
  return ctx;
}

function ConfirmDialog({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger, choices, onClose }) {
  const confirmRef = useRef(null);
  const cancelRef  = useRef(null);
  const returnRef  = useRef(null);
  const dialogRef  = useRef(null);
  const [chosen, setChosen] = useState(choices?.[0]?.value ?? null);
  const done = () => onClose(choices ? chosen : true);

  useEffect(() => {
    returnRef.current = document.activeElement;
    // Destructive actions start on Cancel so Enter can't confirm by accident
    (danger ? cancelRef : confirmRef).current?.focus();
    return () => { returnRef.current?.focus?.(); };
  }, [danger]);

  function onKeyDown(e) {
    // Keep keys inside the dialog (e.g. Esc must not also close a panel behind it)
    e.stopPropagation();
    if (e.key === 'Escape') { e.preventDefault(); onClose(false); }
    if (e.key === 'Tab') {
      // Keep focus inside the dialog
      const items = [...dialogRef.current.querySelectorAll('input, button')].filter(el => !el.disabled);
      const i = items.indexOf(document.activeElement);
      e.preventDefault();
      items[(i + (e.shiftKey ? -1 : 1) + items.length) % items.length]?.focus();
    }
  }

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(false); }}>
      <div ref={dialogRef} style={S.dialog} role="alertdialog" aria-modal="true"
        aria-labelledby="confirm-title" aria-describedby={message ? 'confirm-message' : undefined}
        onKeyDown={onKeyDown}>
        <h3 id="confirm-title" style={S.title}>{emoLabel(title)}</h3>
        {message && <p id="confirm-message" style={S.message}>{message}</p>}
        {choices && (
          <div role="radiogroup" aria-label={title} style={S.choices}>
            {choices.map(c => (
              <label key={c.value} style={{ ...S.choice, ...(chosen === c.value ? S.choiceOn : {}) }}>
                <input type="radio" name="confirm-choice" checked={chosen === c.value} onChange={() => setChosen(c.value)} style={{ marginTop: 3 }} />
                <span><strong>{emoLabel(c.label)}</strong>{c.hint && <span style={S.choiceHint}>{c.hint}</span>}</span>
              </label>
            ))}
          </div>
        )}
        <div style={S.buttons}>
          <button ref={cancelRef} onClick={() => onClose(false)} style={S.cancelBtn}>{emoLabel(cancelLabel)}</button>
          <button ref={confirmRef} onClick={done}
            style={{ ...S.confirmBtn, ...(danger ? S.dangerBtn : {}) }}>{emoLabel(confirmLabel)}</button>
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 2100,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
  },
  dialog: {
    background: 'var(--c-ffffff)', borderRadius: 12, padding: '22px 24px', width: 'min(420px, 100%)',
    boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)', animation: 'badgePop 0.2s ease-out'
  },
  title: { margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--t-263238)', fontFamily: 'inherit' },
  message: { margin: '10px 0 0', fontSize: 14, color: 'var(--t-546e7a)', lineHeight: 1.55, whiteSpace: 'pre-line' },
  buttons: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 },
  cancelBtn: {
    padding: '9px 18px', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-cfd8dc)',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit'
  },
  confirmBtn: {
    padding: '9px 18px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit'
  },
  dangerBtn: { background: 'var(--c-c62828)' },
  choices: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 },
  choice: { display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', border: '1.5px solid var(--c-eceff1)', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: 'var(--t-263238)' },
  choiceOn: { border: '1.5px solid var(--c-80cbc4)', background: 'var(--c-f0faf8)' },
  choiceHint: { display: 'block', fontSize: 12, color: 'var(--t-78909c)', marginTop: 2 },
};
